import React, { useEffect, useState } from "react";
import {
  Container,
  TextField,
  Button,
  Typography,
  Paper,
  Grid,
  Box,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import Header3 from "../../Component/Header3";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";

const UpdateCustomerProfile = () => {
  const [formData, setFormData] = useState({
    customerName: "",
    address: "",
    preferredAddress: "",
    contactNumber: "",
    email: "",
    whatsapp: "",
    username: "",
    password: "",
  });

  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);

  const customerId = localStorage.getItem("customerId");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();

  useEffect(() => {
    axios
      .get(`https://sakhisshop.com/api/customers/${customerId}`)
      .then((res) => setFormData(res.data))
      .catch(() =>
        setToast({
          open: true,
          message: "Failed to load customer data!",
          severity: "error",
        }),
      );
  }, [customerId]);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSendOtp = async () => {
    try {
      await axios.post(
        `https://sakhisshop.com/api/email-verification/send-otp?email=${formData.email}`,
      );
      setToast({
        open: true,
        message: "📨 OTP sent to your email!",
        severity: "info",
      });
      setOtpSent(true);
    } catch {
      setToast({
        open: true,
        message: "❌ Failed to send OTP.",
        severity: "error",
      });
    }
  };

  const handleVerifyOtp = async () => {
    try {
      await axios.post(
        `https://sakhisshop.com/api/email-verification/verify-otp?email=${formData.email}&otp=${otp}`,
      );
      setOtpVerified(true);
      setToast({
        open: true,
        message: "✅ OTP Verified!",
        severity: "success",
      });
    } catch {
      setToast({
        open: true,
        message: "❌ Invalid or expired OTP.",
        severity: "error",
      });
    }
  };

  const handleUpdate = () => {
    if (!otpVerified) {
      setToast({
        open: true,
        message: "⚠️ Please verify OTP before updating!",
        severity: "warning",
      });
      return;
    }

    axios
      .put(`https://sakhisshop.com/api/customers/${customerId}`, formData)
      .then(() => {
        setToast({
          open: true,
          message: "✅ Profile updated successfully!",
          severity: "success",
        });
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      })
      .catch(() =>
        setToast({
          open: true,
          message: "❌ Update failed. Try again!",
          severity: "error",
        }),
      );
  };

  return (
    <Container maxWidth="sm" sx={{ mt: isMobile ? 3 : 6, mb: 4, py: 2 }}>
      <Header3 />

      {/* Back Button */}
      <Box mb={2}>{/* Back Icon */}</Box>

      <Paper
        elevation={6}
        sx={{
          p: isMobile ? 3 : 4,
          borderRadius: 4,
          backgroundColor: "#f4f6f8",
          boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
        }}
      >
        <Typography
          variant={isMobile ? "h5" : "h4"}
          align="center"
          fontWeight={600}
          color="primary"
          gutterBottom
        >
          {" "}
          <IconButton onClick={() => navigate(-1)} color="success">
            <ArrowBackIosNewIcon fontSize="large" />
          </IconButton>
          📝 Update Your Profile
        </Typography>

        <Grid container spacing={2}>
          {[
            { label: "Full Name", name: "customerName" },
            { label: "Address", name: "address" },
            { label: "Preferred Address", name: "preferredAddress" },
            { label: "Contact Number", name: "contactNumber" },
            { label: "Email", name: "email" },
            { label: "Username", name: "username" },
            { label: "Password", name: "password", type: "password" },
          ].map((field) => (
            <Grid item xs={12} key={field.name}>
              <TextField
                fullWidth
                label={field.label}
                name={field.name}
                type={field.type || "text"}
                variant="outlined"
                size="small"
                value={formData[field.name]}
                onChange={handleChange}
                sx={{
                  backgroundColor: "white",
                  borderRadius: 1,
                }}
              />
            </Grid>
          ))}

          {/* OTP section */}
          <Grid item xs={12}>
            {!otpSent ? (
              <Button fullWidth variant="outlined" onClick={handleSendOtp}>
                📤 Send OTP to Email
              </Button>
            ) : (
              <>
                <TextField
                  fullWidth
                  size="small"
                  label="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  sx={{ mb: 1, backgroundColor: "white", borderRadius: 1 }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleVerifyOtp}
                  disabled={otpVerified}
                >
                  {otpVerified ? "✅ OTP Verified" : "Verify OTP"}
                </Button>
              </>
            )}
          </Grid>

          <Grid item xs={12}>
            <Box textAlign="center" mt={2}>
              <Button
                variant="contained"
                size="large"
                onClick={handleUpdate}
                sx={{
                  fontWeight: 600,
                  px: 5,
                  py: 1.3,
                  fontFamily: "'Poppins', sans-serif",
                  backgroundColor: "#1976d2",
                  ":hover": { backgroundColor: "#125a9e" },
                }}
              >
                Update Profile
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Toast Snackbar */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast({ ...toast, open: false })}
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default UpdateCustomerProfile;
